import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Follow-up refetch delays after a 202 Accepted. The per-customer FIFO
 * consumer usually lands in 1–2s, but a live apply-monitor on dev landed
 * ~3.0s after the POST — a few hundred ms after a single 3s refetch — so a
 * second pass at 8s covers the consumer's tail without a manual reload.
 */
export const ASYNC_REFETCH_DELAYS_MS: readonly number[] = [3000, 8000];

/**
 * Reconcile the cache after an out-of-band change: invalidate `keys` now
 * (so any mount or focus refetches), then refetch them at each delay in
 * {@link ASYNC_REFETCH_DELAYS_MS}. Shared by every hook that fronts a
 * 202/async admin route (flags, reactivate, advance status).
 *
 * Pass `delays` for a route whose consumer takes more than one FIFO hop
 * (e.g. generate offer = wipe message → regenerate message).
 */
export function refetchAfterAsyncChange(
  queryClient: QueryClient,
  keys: readonly QueryKey[],
  delays: readonly number[] = ASYNC_REFETCH_DELAYS_MS
): void {
  keys.forEach(queryKey => queryClient.invalidateQueries({ queryKey }));
  delays.forEach(delay => {
    setTimeout(() => {
      keys.forEach(queryKey => queryClient.refetchQueries({ queryKey }));
    }, delay);
  });
}
